import { CHANNELS } from "../../shared/channels";
import { MusicJob, MusicLibrary, MusicTools, Playlist, Track, parseYoutube } from "../../shared/music";
import { call, listen, mediaSrc } from "../bridge";
import { byId, flashToast } from "../dom";
import { iconSvg } from "../icons";
import { t } from "../i18n";
import { isViewActive, onViewHidden, onViewShown, showView } from "../navigation";
import { buzz } from "../tweens";
import { PlayerState, cycleRepeat, initials, labelGradient, next, onPlayer, onTrackLength, playQueue, playerState, previous, refreshQueue, seek, setVolume, setupMediaSession, toggle, toggleShuffle } from "../music_player";

// the music view: a record deck on top, the library under it. the deck always shows what the one app-wide player is
// playing, so it looks right whether the song was started here, from car mode or from the mini player

let library: MusicLibrary = { tracks: [], playlists: [] };
let jobs: MusicJob[] = [];
let tools: MusicTools | null = null;
let tab: "songs" | "playlists" = "songs";
let openList = "";
let search = "";
let editing = "";
let playlistEditing: string | null = null;
let seeking = false;
let carMode = false;
let wake: { release(): Promise<void> } | null = null;
let armTimer = 0;
let lastTrack = "";
let busy = 0;

const PLAY = '<path d="M8 5v14l11-7z"/>';
const PAUSE = '<path d="M7 5h4v14H7zM13 5h4v14h-4z"/>';

function say(text: string): void {
  byId("mu-live").textContent = text;
}

function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

// a song's cover: its picture, or a coloured label made from its name
function paintCover(element: HTMLElement, track: Track | null): void {
  element.style.setProperty("--art", track?.coverUrl ? `url("${mediaSrc(track.coverUrl)}") center / cover` : labelGradient(track?.title || "chix"));
  element.dataset.initials = track?.coverUrl ? "" : initials(track?.title || "");
}

// --- loading

async function reload(): Promise<void> {
  try {
    const [lib, list] = await Promise.all([call<MusicLibrary>(CHANNELS.musicLibrary), call<MusicJob[]>(CHANNELS.musicJobs)]);
    library = lib;
    jobs = list;
    refreshQueue(library.tracks);
    render();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not load the music"));
  }
}

async function loadTools(): Promise<void> {
  try {
    tools = await call<MusicTools>(CHANNELS.musicTools);
  } catch {
    tools = null;
  }
  paintTools();
}

// --- the deck

function paintDeck(state: PlayerState, what: string): void {
  const track = state.track;
  const disc = byId("mu-disc");
  disc.classList.toggle("on", state.playing);
  byId("mu-led").classList.toggle("on", state.playing);
  byId("mu-play-icon").innerHTML = state.playing ? PAUSE : PLAY;
  byId("mu-play").setAttribute("aria-label", state.playing ? t("pause") : t("play"));
  byId("mu-car-play-icon").innerHTML = state.playing ? PAUSE : PLAY;
  byId("mu-car-play").setAttribute("aria-label", state.playing ? t("pause") : t("play"));
  if (what === "track" || what === "queue" || lastTrack !== (track?.id || "")) {
    const changed = lastTrack !== (track?.id || "");
    lastTrack = track?.id || "";
    byId("mu-title").textContent = track?.title || t("nothing playing");
    byId("mu-artist").textContent = track ? [track.artist, track.album].filter(Boolean).join(" · ") || t("unknown artist") : t("pick a song or add one");
    byId("mu-car-title").textContent = track?.title || t("nothing playing");
    byId("mu-car-artist").textContent = track?.artist || "";
    paintCover(byId("mu-label"), track);
    byId("mu-label").textContent = track && !track.coverUrl ? initials(track.title) : "";
    if (changed) liftArm();
    paintMini(state);
    markPlaying(track?.id || "");
    say(track ? `${track.title}${track.artist ? `, ${track.artist}` : ""}` : "");
  }
  if (!seeking) {
    const length = state.duration || 0;
    const slider = byId<HTMLInputElement>("mu-seek");
    slider.value = String(length ? Math.round((state.time / length) * 1000) : 0);
    slider.setAttribute("aria-valuetext", `${clock(state.time)} of ${clock(length)}`);
    byId("mu-time").textContent = clock(state.time);
    byId("mu-length").textContent = clock(length);
    byId("mu-car-bar").style.setProperty("--at", String(length ? state.time / length : 0));
  }
  // the arm creeps in towards the middle as the song goes
  const progress = state.duration ? Math.min(1, state.time / state.duration) : 0;
  byId("mu-arm").style.setProperty("--a", state.playing && !armTimer ? `${6 + progress * 16}deg` : "-22deg");
  byId("mu-shuffle").setAttribute("aria-pressed", String(state.shuffle));
  byId("mu-repeat").setAttribute("aria-pressed", String(state.repeat !== "off"));
  byId("mu-repeat").setAttribute("aria-label", state.repeat === "one" ? t("repeat one song") : state.repeat === "all" ? t("repeat everything") : t("repeat off"));
  byId("mu-repeat").dataset.mode = state.repeat;
  if (document.activeElement !== byId("mu-volume")) byId<HTMLInputElement>("mu-volume").value = String(Math.round(state.volume * 100));
}

// when the song changes the arm lifts off, then swings back down onto the new record
function liftArm(): void {
  window.clearTimeout(armTimer);
  byId("mu-arm").style.setProperty("--a", "-22deg");
  armTimer = window.setTimeout(() => {
    armTimer = 0;
    paintDeck(playerState(), "time");
  }, 650);
}

function markPlaying(id: string): void {
  document.querySelectorAll<HTMLElement>("#view-music .mu-row[data-id]").forEach((row) => {
    row.setAttribute("aria-current", String(row.dataset.id === id));
  });
}

// --- the mini player, on every other screen

function paintMini(state: PlayerState): void {
  const mini = byId("mini-player");
  mini.hidden = !state.track || isViewActive("music");
  if (!state.track) return;
  byId("mini-title").textContent = state.track.title;
  byId("mini-artist").textContent = state.track.artist;
  paintCover(byId("mini-disc"), state.track);
  byId("mini-disc").classList.toggle("on", state.playing);
  byId("mini-play-icon").innerHTML = state.playing ? PAUSE : PLAY;
  byId("mini-play").setAttribute("aria-label", state.playing ? t("pause") : t("play"));
}

// --- the library

function filtered(): Track[] {
  const needle = search.trim().toLowerCase();
  return needle ? library.tracks.filter((track) => `${track.title} ${track.artist} ${track.album}`.toLowerCase().includes(needle)) : library.tracks;
}

function trackRow(track: Track, queue: Track[]): HTMLElement {
  const row = document.createElement("li");
  row.className = "mu-row";
  row.dataset.id = track.id;
  row.setAttribute("aria-current", String(playerState().track?.id === track.id));
  const play = document.createElement("button");
  play.type = "button";
  play.className = "mu-play-row";
  const cover = document.createElement("span");
  cover.className = "mu-cover";
  paintCover(cover, track);
  cover.textContent = track.coverUrl ? "" : initials(track.title);
  const text = document.createElement("span");
  text.className = "mu-text";
  const title = document.createElement("b");
  title.textContent = track.title;
  const sub = document.createElement("small");
  sub.textContent = [track.artist, track.album].filter(Boolean).join(" · ");
  text.append(title, sub);
  const length = document.createElement("time");
  length.textContent = track.duration ? clock(track.duration) : "";
  play.append(cover, text, length);
  play.setAttribute("aria-label", `${t("play")} ${track.title}${track.artist ? `, ${track.artist}` : ""}`);
  play.addEventListener("click", () => {
    buzz();
    playQueue(queue, queue.findIndex((item) => item.id === track.id));
  });
  const more = document.createElement("button");
  more.type = "button";
  more.className = "mu-more";
  more.innerHTML = iconSvg("more", 20);
  more.setAttribute("aria-label", t("details for {name}", { name: track.title }));
  more.addEventListener("click", () => openTrackSheet(track.id));
  row.append(play, more);
  return row;
}

function playlistTracks(playlist: Playlist): Track[] {
  const byId = new Map(library.tracks.map((track) => [track.id, track]));
  return playlist.trackIds.map((id) => byId.get(id)).filter((track): track is Track => Boolean(track));
}

function playlistCard(playlist: Playlist): HTMLElement {
  const card = document.createElement("li");
  card.className = "mu-row mu-list-row";
  const open = document.createElement("button");
  open.type = "button";
  open.className = "mu-play-row";
  const tracks = playlistTracks(playlist);
  const collage = document.createElement("span");
  collage.className = "mu-cover mu-collage";
  tracks.slice(0, 4).forEach((track) => {
    const tile = document.createElement("i");
    paintCover(tile, track);
    collage.append(tile);
  });
  if (!tracks.length) collage.textContent = "♪";
  const text = document.createElement("span");
  text.className = "mu-text";
  const title = document.createElement("b");
  title.textContent = playlist.name;
  const sub = document.createElement("small");
  sub.textContent = t("{count} songs", { count: tracks.length });
  text.append(title, sub);
  open.append(collage, text);
  open.addEventListener("click", () => {
    openList = playlist.id;
    render();
    byId("mu-library").scrollIntoView({ block: "nearest" });
  });
  card.append(open);
  return card;
}

function render(): void {
  const total = library.tracks.length;
  byId("mu-count").textContent = total ? t("{count} songs", { count: total }) : "";
  byId("mu-tab-songs").setAttribute("aria-selected", String(tab === "songs"));
  byId("mu-tab-playlists").setAttribute("aria-selected", String(tab === "playlists"));
  paintJobs();
  const list = byId("mu-list");
  const head = byId("mu-list-head");
  const playlist = openList ? library.playlists.find((item) => item.id === openList) : undefined;
  if (openList && !playlist) openList = "";
  byId("mu-search-row").hidden = tab !== "songs" || Boolean(playlist);
  if (tab === "playlists" && playlist) {
    const tracks = playlistTracks(playlist);
    head.hidden = false;
    byId("mu-pl-name").textContent = playlist.name;
    byId("mu-pl-count").textContent = t("{count} songs", { count: tracks.length });
    list.replaceChildren(...tracks.map((track) => trackRow(track, tracks)));
    byId("mu-empty").hidden = tracks.length > 0;
    byId("mu-empty").textContent = t("this playlist is empty. open a song's details to add it here");
    return;
  }
  head.hidden = true;
  if (tab === "playlists") {
    list.replaceChildren(...library.playlists.map(playlistCard));
    byId("mu-empty").hidden = library.playlists.length > 0;
    byId("mu-empty").textContent = t("no playlists yet");
    return;
  }
  const shown = filtered();
  list.replaceChildren(...shown.map((track) => trackRow(track, shown)));
  byId("mu-empty").hidden = shown.length > 0;
  byId("mu-empty").textContent = total ? t("nothing matches that") : t("no songs yet. add a youtube link or upload some files");
}

function paintJobs(): void {
  const box = byId("mu-jobs");
  box.hidden = jobs.length === 0;
  box.replaceChildren(...jobs.slice(0, 6).map((job) => {
    const row = document.createElement("li");
    row.className = `mu-job ${job.state}`;
    if (job.state === "running" || job.state === "queued") {
      const spin = document.createElement("i");
      spin.setAttribute("aria-hidden", "true");
      row.append(spin);
    }
    const text = document.createElement("span");
    text.textContent = job.state === "failed" ? `${t("could not get it")}: ${job.message}` : job.state === "done" ? `${t("added")}: ${job.message}` : `${job.message}…`;
    row.append(text);
    return row;
  }));
}

function paintTools(): void {
  const note = byId("mu-tools-note");
  const missing = tools !== null && !tools.ytdlp;
  note.hidden = !missing;
  note.textContent = missing ? t("downloading from youtube is not set up on this server yet. uploads work. whoever runs the server needs to install yt-dlp") : "";
  byId<HTMLButtonElement>("mu-get").disabled = missing;
}

// --- adding songs

async function getLink(): Promise<void> {
  const input = byId<HTMLInputElement>("mu-link");
  const link = input.value.trim();
  if (!link) return;
  if (!parseYoutube(link)) return flashToast(t("that is not a youtube link"));
  try {
    jobs = await call<MusicJob[]>(CHANNELS.musicYoutube, link);
    input.value = "";
    paintJobs();
    say(t("added to the downloads"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not start that"));
  }
}

// how long an audio file is, read from the file itself before it is sent
function lengthOf(file: File): Promise<number> {
  return new Promise((resolve) => {
    const probe = document.createElement("audio");
    const url = URL.createObjectURL(file);
    const done = (value: number) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    probe.preload = "metadata";
    probe.onloadedmetadata = () => done(Number.isFinite(probe.duration) ? Math.round(probe.duration) : 0);
    probe.onerror = () => done(0);
    window.setTimeout(() => done(0), 8000);
    probe.src = url;
  });
}

function dataUrlOf(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(t("could not read that file")));
    reader.readAsDataURL(file);
  });
}

async function upload(files: File[]): Promise<void> {
  const songs = files.filter((file) => file.type.startsWith("audio/") || /\.(mp3|m4a|aac|ogg|oga|opus|wav|flac)$/i.test(file.name));
  if (!songs.length) return flashToast(t("use mp3, m4a, ogg, wav or flac files"));
  busy++;
  const status = byId("mu-busy");
  status.hidden = false;
  let added = 0;
  try {
    for (const [at, file] of songs.entries()) {
      status.textContent = t("adding {done} of {total}: {name}", { done: at + 1, total: songs.length, name: file.name });
      try {
        const length = await lengthOf(file);
        await call<Track>(CHANNELS.musicUpload, file.name, await dataUrlOf(file), length);
        added++;
      } catch (error) {
        flashToast(`${file.name}: ${error instanceof Error ? error.message : t("could not add it")}`);
      }
    }
    say(t("{count} songs added", { count: added }));
  } finally {
    busy--;
    status.hidden = busy === 0;
    await reload();
  }
}

// --- one song's details

function openTrackSheet(id: string): void {
  const track = library.tracks.find((item) => item.id === id);
  if (!track) return;
  editing = id;
  byId<HTMLInputElement>("mu-edit-title").value = track.title;
  byId<HTMLInputElement>("mu-edit-artist").value = track.artist;
  byId<HTMLInputElement>("mu-edit-album").value = track.album;
  paintCover(byId("mu-edit-cover"), track);
  byId("mu-edit-cover").textContent = track.coverUrl ? "" : initials(track.title);
  byId("mu-edit-cover-clear").hidden = !track.coverUrl;
  byId("mu-edit-source").hidden = track.source !== "youtube";
  const lists = byId("mu-edit-lists");
  lists.replaceChildren(...library.playlists.map((playlist) => {
    const label = document.createElement("label");
    label.className = "mu-check";
    const box = document.createElement("input");
    box.type = "checkbox";
    box.checked = playlist.trackIds.includes(id);
    box.addEventListener("change", () => void togglePlaylistTrack(playlist, id, box.checked));
    const name = document.createElement("span");
    name.textContent = playlist.name;
    label.append(box, name);
    return label;
  }));
  byId("mu-edit-lists-wrap").hidden = library.playlists.length === 0;
  byId("mu-track-sheet").hidden = false;
  byId<HTMLInputElement>("mu-edit-title").focus();
}

async function closeTrackSheet(): Promise<void> {
  const id = editing;
  editing = "";
  byId("mu-track-sheet").hidden = true;
  const track = library.tracks.find((item) => item.id === id);
  if (!track) return;
  const title = byId<HTMLInputElement>("mu-edit-title").value.trim() || track.title;
  const artist = byId<HTMLInputElement>("mu-edit-artist").value.trim();
  const album = byId<HTMLInputElement>("mu-edit-album").value.trim();
  if (title === track.title && artist === track.artist && album === track.album) return;
  try {
    await call<Track>(CHANNELS.musicSaveTrack, { id, title, artist, album });
    await reload();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save that"));
  }
}

async function togglePlaylistTrack(playlist: Playlist, id: string, on: boolean): Promise<void> {
  const trackIds = on ? [...playlist.trackIds, id] : playlist.trackIds.filter((item) => item !== id);
  try {
    await call<Playlist>(CHANNELS.musicSavePlaylist, { id: playlist.id, name: playlist.name, trackIds });
    playlist.trackIds = trackIds;
    say(on ? t("added to {name}", { name: playlist.name }) : t("taken out of {name}", { name: playlist.name }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save that"));
  }
}

// a cover picture shrunk to a sensible size before it is sent
async function shrinkCover(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(1, 600 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * side));
  canvas.height = Math.max(1, Math.round(bitmap.height * side));
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return canvas.toDataURL("image/jpeg", 0.9);
}

async function changeCover(file: File | undefined, clear: boolean): Promise<void> {
  if (!editing) return;
  try {
    const data = clear ? true : await shrinkCover(file!);
    await call<Track>(CHANNELS.musicSaveTrack, { id: editing, cover: data });
    await reload();
    if (editing) openTrackSheet(editing);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not change the cover"));
  }
}

async function removeSong(): Promise<void> {
  const track = library.tracks.find((item) => item.id === editing);
  if (!track || !confirm(t("delete {name}? this can't be undone", { name: track.title }))) return;
  editing = "";
  byId("mu-track-sheet").hidden = true;
  try {
    await call(CHANNELS.musicRemoveTrack, track.id);
    await reload();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not delete it"));
  }
}

// --- playlists

function openPlaylistSheet(id: string | null): void {
  playlistEditing = id;
  const playlist = id ? library.playlists.find((item) => item.id === id) : undefined;
  byId("mu-pl-sheet-title").textContent = playlist ? t("rename playlist") : t("new playlist");
  byId<HTMLInputElement>("mu-pl-input").value = playlist?.name || "";
  byId("mu-pl-delete").hidden = !playlist;
  byId("mu-pl-sheet").hidden = false;
  byId<HTMLInputElement>("mu-pl-input").focus();
}

async function savePlaylistSheet(): Promise<void> {
  const name = byId<HTMLInputElement>("mu-pl-input").value.trim();
  if (!name) return;
  const existing = playlistEditing ? library.playlists.find((item) => item.id === playlistEditing) : undefined;
  byId("mu-pl-sheet").hidden = true;
  // made from a song's details, the new playlist starts with that song and the details stay open
  const fromSong = !existing && editing ? editing : "";
  try {
    const saved = await call<Playlist>(CHANNELS.musicSavePlaylist, { id: existing?.id, name, trackIds: existing?.trackIds || (fromSong ? [fromSong] : []) });
    if (!fromSong) {
      tab = "playlists";
      openList = saved.id;
    }
    await reload();
    if (fromSong) openTrackSheet(fromSong);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save that"));
  }
}

async function deletePlaylist(): Promise<void> {
  const playlist = playlistEditing ? library.playlists.find((item) => item.id === playlistEditing) : undefined;
  if (!playlist || !confirm(t("delete the playlist {name}? the songs stay in your library", { name: playlist.name }))) return;
  byId("mu-pl-sheet").hidden = true;
  openList = "";
  try {
    await call(CHANNELS.musicRemovePlaylist, playlist.id);
    await reload();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not delete it"));
  }
}

function playPlaylist(shuffleFirst: boolean): void {
  const playlist = library.playlists.find((item) => item.id === openList);
  const tracks = playlist ? playlistTracks(playlist) : [];
  if (!tracks.length) return;
  if (shuffleFirst && !playerState().shuffle) toggleShuffle();
  if (!shuffleFirst && playerState().shuffle) toggleShuffle();
  playQueue(tracks, shuffleFirst ? Math.floor(Math.random() * tracks.length) : 0);
}

// --- car mode: four huge keys and a few big places to start from, for a phone on a dash mount

async function setCarMode(on: boolean): Promise<void> {
  carMode = on;
  byId("mu-car-ui").hidden = !on;
  byId("mu-car").setAttribute("aria-pressed", String(on));
  document.body.classList.toggle("car-open", on);
  if (on) {
    paintCarStarts();
    byId("mu-car-play").focus();
    try {
      const lock = (navigator as Navigator & { wakeLock?: { request(kind: "screen"): Promise<{ release(): Promise<void> }> } }).wakeLock;
      wake = (await lock?.request("screen")) || null;
    } catch {
      wake = null;
    }
  } else {
    void wake?.release().catch(() => undefined);
    wake = null;
    byId("mu-car").focus();
  }
}

function paintCarStarts(): void {
  const start = (label: string, tracks: Track[], shuffleOn: boolean) => {
    const key = document.createElement("button");
    key.type = "button";
    key.textContent = label;
    key.disabled = tracks.length === 0;
    key.addEventListener("click", () => {
      if (playerState().shuffle !== shuffleOn) toggleShuffle();
      playQueue(tracks, shuffleOn ? Math.floor(Math.random() * tracks.length) : 0);
    });
    return key;
  };
  byId("mu-car-starts").replaceChildren(
    start(t("all songs"), library.tracks, false),
    start(t("shuffle everything"), library.tracks, true),
    ...library.playlists.slice(0, 10).map((playlist) => start(playlist.name, playlistTracks(playlist), false))
  );
}

export function setupMusicView(): void {
  setupMediaSession();
  onTrackLength((id, seconds) => {
    const track = library.tracks.find((item) => item.id === id);
    if (track && !track.duration) void call(CHANNELS.musicSaveTrack, { id, duration: seconds }).catch(() => undefined);
  });
  onPlayer((state, what) => {
    paintDeck(state, what);
    if (what === "track") paintMini(state);
    else if (what === "play") paintMini(state);
  });
  paintDeck(playerState(), "track");

  byId("mu-play").addEventListener("click", toggle);
  byId("mu-car-play").addEventListener("click", toggle);
  byId("mu-next").addEventListener("click", next);
  byId("mu-car-next").addEventListener("click", next);
  byId("mu-prev").addEventListener("click", previous);
  byId("mu-car-prev").addEventListener("click", previous);
  byId("mu-shuffle").addEventListener("click", toggleShuffle);
  byId("mu-repeat").addEventListener("click", cycleRepeat);
  byId("mini-play").addEventListener("click", toggle);
  byId("mini-next").addEventListener("click", next);
  byId("mini-open").addEventListener("click", () => showView("music"));

  const slider = byId<HTMLInputElement>("mu-seek");
  slider.addEventListener("input", () => {
    seeking = true;
    const length = playerState().duration;
    byId("mu-time").textContent = clock((Number(slider.value) / 1000) * length);
  });
  slider.addEventListener("change", () => {
    seeking = false;
    seek((Number(slider.value) / 1000) * playerState().duration);
  });
  byId<HTMLInputElement>("mu-volume").addEventListener("input", (event) => setVolume(Number((event.target as HTMLInputElement).value) / 100));

  byId("mu-tab-songs").addEventListener("click", () => {
    tab = "songs";
    openList = "";
    render();
  });
  byId("mu-tab-playlists").addEventListener("click", () => {
    tab = "playlists";
    openList = "";
    render();
  });
  byId<HTMLInputElement>("mu-search").addEventListener("input", (event) => {
    search = (event.target as HTMLInputElement).value;
    render();
  });
  byId("mu-add").addEventListener("click", () => {
    const open = byId("mu-add-sheet").hidden;
    byId("mu-add-sheet").hidden = !open;
    if (open) {
      void loadTools();
      byId<HTMLInputElement>("mu-link").focus();
    }
  });
  byId("mu-get").addEventListener("click", () => void getLink());
  byId<HTMLInputElement>("mu-link").addEventListener("keydown", (event) => {
    if (event.key === "Enter") void getLink();
  });
  byId("mu-pick").addEventListener("click", () => byId<HTMLInputElement>("mu-files").click());
  byId<HTMLInputElement>("mu-files").addEventListener("change", (event) => {
    const input = event.target as HTMLInputElement;
    void upload(Array.from(input.files || []));
    input.value = "";
  });
  const page = byId("view-music");
  page.addEventListener("dragover", (event) => {
    if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
  });
  page.addEventListener("drop", (event) => {
    if (!event.dataTransfer?.files.length) return;
    event.preventDefault();
    void upload(Array.from(event.dataTransfer.files));
  });

  byId("mu-pl-new").addEventListener("click", () => openPlaylistSheet(null));
  byId("mu-pl-rename").addEventListener("click", () => openPlaylistSheet(openList));
  byId("mu-pl-back").addEventListener("click", () => {
    openList = "";
    render();
  });
  byId("mu-pl-play").addEventListener("click", () => playPlaylist(false));
  byId("mu-pl-shuffle").addEventListener("click", () => playPlaylist(true));
  byId("mu-pl-save").addEventListener("click", () => void savePlaylistSheet());
  byId("mu-pl-delete").addEventListener("click", () => void deletePlaylist());
  byId("mu-pl-close").addEventListener("click", () => { byId("mu-pl-sheet").hidden = true; });
  byId<HTMLInputElement>("mu-pl-input").addEventListener("keydown", (event) => {
    if (event.key === "Enter") void savePlaylistSheet();
  });

  byId("mu-edit-close").addEventListener("click", () => void closeTrackSheet());
  byId("mu-track-sheet").addEventListener("click", (event) => {
    if (event.target === byId("mu-track-sheet")) void closeTrackSheet();
  });
  byId("mu-edit-cover-pick").addEventListener("click", () => byId<HTMLInputElement>("mu-edit-cover-file").click());
  byId<HTMLInputElement>("mu-edit-cover-file").addEventListener("change", (event) => {
    const input = event.target as HTMLInputElement;
    void changeCover(input.files?.[0], false);
    input.value = "";
  });
  byId("mu-edit-cover-clear").addEventListener("click", () => void changeCover(undefined, true));
  byId("mu-edit-remove").addEventListener("click", () => void removeSong());
  byId("mu-edit-playlist-new").addEventListener("click", () => openPlaylistSheet(null));
  for (const id of ["mu-track-sheet", "mu-pl-sheet"]) {
    byId(id).addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      if (id === "mu-track-sheet") void closeTrackSheet();
      else byId("mu-pl-sheet").hidden = true;
    });
  }

  byId("mu-car").addEventListener("click", () => void setCarMode(!carMode));
  byId("mu-car-close").addEventListener("click", () => void setCarMode(false));
  window.addEventListener("keydown", (event) => {
    if (carMode && event.key === "Escape") void setCarMode(false);
    // space plays and pauses on this screen, unless a field or button has it
    if (event.key === " " && isViewActive("music") && !(event.target as HTMLElement | null)?.closest("input, textarea, select, button, a, [role='slider']")) {
      event.preventDefault();
      toggle();
    }
  });

  listen(CHANNELS.musicUpdated, () => {
    if (busy === 0) void reload();
    else void call<MusicJob[]>(CHANNELS.musicJobs).then((list) => { jobs = list; paintJobs(); }).catch(() => undefined);
  });
  onViewShown("music", () => {
    paintMini(playerState());
    void reload();
  });
  onViewHidden("music", () => {
    // the view still counts as current while this runs, so the mini player looks a moment later
    window.setTimeout(() => paintMini(playerState()), 0);
    if (carMode) void setCarMode(false);
  });
}
